import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import { PUBLISHER_NAME } from '@/constants/reporting';
import { getDayOfWeek } from './dateFormatter';
import { createPublisherLogoMapper } from './reportingDashboard/slice';
import { isRoleAllowedWhitelabelSelect } from './roles';

export const createArrayWithDate = (arrayOfElements, selectedPeriods) => {
  const newObj = {};
  const newArray = [];
  let totalImpressions = 0;

  arrayOfElements.forEach(element => {
    const existingField = newObj[element.date];
    totalImpressions += element.impressions;
    newObj[element.date] = existingField ? existingField + element.impressions : element.impressions;
  });

  selectedPeriods.forEach(item => {
    newArray.push({
      Day: item,
      Impressions: newObj[item] || 0,
    });
  });

  newArray.push({ Day: null, Impressions: totalImpressions });
  return newArray;
};

const getDaysArray = function (start, end) {
  const arr = [];
  for (let dt = new Date(start); dt <= new Date(end); dt.setDate(dt.getDate() + 1)) {
    arr.push(new Date(dt));
  }
  return arr;
};

const getRibeyeAdvertiserIds = advertisers => {
  const ids = [];
  advertisers.forEach(({ ribeyeAdvertiserId, value }) => {
    const id = ribeyeAdvertiserId ?? value;
    if (id != null) {
      ids.push(id);
    }
  });
  return ids;
};

const mapClients = clients => {
  return clients.map(cl => getRibeyeAdvertiserIds(cl.advertisers)).flat();
};

const mapWhitelabels = whitelabels => {
  return whitelabels.map(wl => mapClients(wl.clients)).flat();
};

const setCurrentAdvertisers = (filters, role, clients, whitelabels) => {
  if (filters.advertisers?.length) {
    return getRibeyeAdvertiserIds(filters.advertisers);
  }
  if (filters.clients?.length) {
    return mapClients(filters.clients);
  }
  if (isRoleAllowedWhitelabelSelect(role)) {
    if (filters.whitelabels?.length) {
      return mapWhitelabels(filters.whitelabels);
    }
    return mapWhitelabels(whitelabels);
  }
  return mapClients(clients);
};

const dateToObj = date => `${date}T05:00:00`;

export const mapFilter = (filters, role, clients, whitelabels, isHourGrouping) => {
  const advertiserIds = setCurrentAdvertisers(filters, role, clients, whitelabels);
  const campaignIds = filters.campaigns.map(item => item.value);
  const daylist = getDaysArray(new Date(dateToObj(filters.dateStart)), new Date(dateToObj(filters.dateEnd)));

  const selectedPeriods = daylist.map(value => value.toISOString().slice(0, 10));
  const appliedFilter = {
    dateGrouping: isHourGrouping ? 'HOURS' : 'DAYS',
    groupingBy: isHourGrouping
      ? []
      : ['AGENCY', 'ADVERTISER', 'CAMPAIGN', 'LINE_ITEM', 'CREATIVE', 'PUBLISHER', 'VENUE_TYPE'],
    startDate: `${filters.dateStart}T00:00:00Z`,
    endDate: `${filters.dateEnd}T23:59:59Z`,
    advertiserIds,
    campaignIds,
    lineItems: [],
    creatives: [],
    screens: [],
    playsVerified: true,
  };

  return { appliedFilter, selectedPeriods };
};

export const createArrayWithField = (arrayOfElements, fieldName = 'advertiserName', newName = 'Advertiser') => {
  const newObj = {};
  const newArray = [];

  arrayOfElements.forEach(element => {
    const existingField = newObj[element[fieldName]];
    newObj[element[fieldName]] = existingField ? existingField + element.totalImpressions : element.totalImpressions;
  });

  for (const [key, value] of Object.entries(newObj)) {
    newArray.push({
      [newName]: key,
      Impressions: value,
    });
  }

  return newArray.sort((a, b) => b.Impressions - a.Impressions);
};

export const createPublisherArray = (arrayOfElements, publishers) => {
  const publisherLogoMapper = createPublisherLogoMapper(publishers);
  const baseArray = createArrayWithField(arrayOfElements, 'publisherName', PUBLISHER_NAME);

  return baseArray.map(item => ({
    ...item,
    Logo: publisherLogoMapper[item[PUBLISHER_NAME]] || null,
  }));
};

export const createDayOfWeekArray = arrayOfElements => {
  const currentArray = arrayOfElements.filter(item => item.Day);
  const newObj = {};

  currentArray.forEach(element => {
    const dayNumber = getDayOfWeek(element['Day']);
    const existingField = newObj[dayNumber];
    newObj[dayNumber] = existingField ? existingField + element.Impressions : element.Impressions;
  });

  const processedResultArray = [];
  for (const [key, value] of Object.entries(newObj)) {
    processedResultArray.push({
      [BeeswaxReportProperties.DAY_OF_WEEK]: key,
      [BeeswaxReportProperties.IMPRESSIONS]: value || 0,
    });
  }

  return processedResultArray;
};

export const createCampainsListWithAdvertiser = arrayOfCampains => {
  const newObj = {};
  arrayOfCampains.forEach(item => {
    const key = item.ribeyeAdvertiserId;
    const option = { value: item.id, label: item.name };
    if (newObj[key]) {
      newObj[key].push(option);
    } else {
      newObj[key] = [option];
    }
  });
  return newObj;
};
